//External libs
import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import ReadingsBatchEntriesModel from './readingsBatchEntries.model';

/**
 * @service ReadingsBatchEntriesService
 * @description Service for managing batch entries of readings within the aquaculture system.
 * This service facilitates the retrieval of grouped reading data, often organized by 'batches'
 * corresponding to specific sections of a farm.
 *
 * Batch entries allow for efficient data entry and retrieval where multiple readings (e.g., for different ponds in a section)
 * are recorded or reviewed simultaneously.
 *
 * The service interacts with the `/aqu/batchentries/` backend endpoint.
 *
 * **Key Responsibilities:**
 * 1. **Batch Retrieval**: Fetching lists of reading batches filtered by section.
 * 2. **Data Inclusion**: Automatically including related reading details in the response.
 * 3. **Resource Management**: Handling HTTP request lifecycles to prevent memory leaks.
 *
 * @feature 1. Retrieve batch readings by Section ID.
 * @feature 2. Robust management of concurrent HTTP requests.
 *
 * @architecture
 * Extends base Service<ReadingsBatchEntriesModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /aqu/batchentries/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class ReadingsBatchEntriesService extends Service<ReadingsBatchEntriesModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @type {string}
   * @description Base URL for reading-specific operations.
   * Currently declared but not explicitly initialized in the constructor beyond the super call.
   */
  public readingurl: string;

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/aqu/batchentries/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/blubay/batchentries/');
  }

  /**
   * @method getBatchReadingsList
   * @async
   * @description Fetch a list of reading batch entries for a specific section.
   *
   * This method retrieves batch records associated with a section ID. It explicitly requests
   * the inclusion of related 'readings' data in the response (`include=readings`).
   *
   * @param {string} sectionId - The unique identifier of the pond section.
   * @returns {Promise<Array<ReadingsBatchEntriesModel>>} Promise resolving to an array of batch entry models.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * readingsBatchEntriesService.getBatchReadingsList('sec-123').then(batches => {
   *   console.log('Batch Entries:', batches);
   * });
   */
  public getBatchReadingsList(sectionId: string) {
    return new Promise<Array<ReadingsBatchEntriesModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<ReadingsBatchEntriesModel>>(`${this.baseUrl}${this.serviceUrl}?section_id=${sectionId}&include=readings&pageSize=20000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
